import "./gaugeconrate.css";

/*
  GaugeConRate — Productivity Index Gauge
  ─────────────────────────────────────────
  Props:
    conversionRate — number  0–100
    label          — string  override auto label (optional)

  Semicircle: starts at the LEFT (180°), sweeps clockwise to RIGHT (0°).
  Using standard SVG angles: 0° = right, 90° = bottom, 180° = left.
  Track: 180° → 0°  (full semicircle, sweep-flag=1)
  Fill:  180° → (180° - rate/100 * 180°)
*/

function getConfig(rate) {
  if (rate < 30) return { label: "Low", color: "#d85a30", track: "#fddcb8", bg: "#fff3e8" };
  if (rate < 60) return { label: "Moderate", color: "#ba7517", track: "#fde8a0", bg: "#fffae8" };
  if (rate < 80) return { label: "Good", color: "#1d9e75", track: "#a3e0c4", bg: "#e8f7ee" };
  return { label: "Excellent", color: "#0f6e56", track: "#7dd4b8", bg: "#e0f5ef" };
}

/* Convert polar angle (degrees, standard SVG) to x,y on circle */
function pt(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/*
  Arc from startDeg → endDeg, clockwise (sweep=1).
  Start = 180° (left), End = 0° (right) for full track.
  For fill: start = 180°, end = 180° - (pct * 180°)
*/
function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = pt(cx, cy, r, startDeg);
  const e = pt(cx, cy, r, endDeg);
  /* large-arc: 1 if span > 180° */
  let span = endDeg - startDeg;
  if (span < 0) span += 360;
  const large = span > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

export default function GaugeConRate({ conversionRate = 0, label }) {
  const rate = Math.min(100, Math.max(0, conversionRate * 10));
  const cfg = getConfig(rate);
  const display = label || cfg.label;

  const CX = 100,
    CY = 90,
    R = 68,
    SW = 14;

  /* Track: full semicircle 180° → 360° (=0°) clockwise */
  const trackD = arcPath(CX, CY, R, 180, 360);

  /* Fill: from 180° clockwise by (rate/100 * 180°)
     endDeg = 180 + (rate/100)*180 */
  const fillEndDeg = 180 + (rate / 100) * 180;
  const fillD = rate > 0 ? arcPath(CX, CY, R, 180, fillEndDeg) : null;

  return (
    <div className='gcr-wrap'>
      <svg className='gcr-svg' viewBox='26 20 148 90' aria-label={`Productivity index: ${rate}% — ${display}`} overflow='visible'>
        {/* Track */}
        <path d={trackD} fill='none' stroke={cfg.track} strokeWidth={SW} strokeLinecap='round' />
        {/* Fill */}
        {fillD && <path d={fillD} fill='none' stroke={cfg.color} strokeWidth={SW} strokeLinecap='round' />}
        {/* Score */}
        <text x={CX} y={CY - 8} textAnchor='middle' className='gcr-score' fill={cfg.color}>
          {rate}
        </text>
        {/* Label */}
        <text x={CX} y={CY + 10} textAnchor='middle' className='gcr-label' fill='#7a8fab'>
          {display.toUpperCase()}
        </text>
      </svg>

      {/* Axis labels */}
      <div className='gcr-axis'>
        <span>0</span>
        <span>100</span>
      </div>

      {/* Pill */}
      <div className='gcr-pill' style={{ background: cfg.bg, color: cfg.color }}>
        <span className='gcr-dot' style={{ background: cfg.color }} />
        {display} performance
      </div>
    </div>
  );
}
